import { Controller } from 'stimulus'
import { hasAnyOpenLayer } from '~/common/helpers/esc_stack'

// Keyboard-shortcut dispatcher. Reads its binding table from ChatBot::Command
// (serialized server-side into commandsValue) and turns a matching keydown into the same DOM
// event a palette row's click would dispatch — one path, two triggers. Escape is handled
// entirely by esc_stack.js, not here; this controller never matches it. ENG-33816.
const IS_MAC =
  typeof navigator === 'object' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform)

const pageComposers = new Set()
let activePageComposer = null

export default class extends Controller {
  static values = {
    commands: Array,
    scope: { type: String, default: 'global' },
    primary: Boolean,
  }

  get composerScope() {
    return this.scopeValue === 'composer'
  }

  get listenerTarget() {
    return this.composerScope ? this.element : window
  }

  connect() {
    // Capture within this composer before TipTap can consume a formatting shortcut.
    this.listenerTarget.addEventListener(
      'keydown',
      this.#handleKeydown,
      this.composerScope
    )
    // The Coach apps' menus (⌘I, ⌘N, ⌘F) take those keys before the page sees them; they run
    // the same commands here, under the same rules as a keystroke.
    if (!this.composerScope) window.SimpleroCoachCommands = { run: this.#run }
    if (this.composerScope && this.primaryValue) {
      pageComposers.add(this)
      window.addEventListener('keydown', this.#handlePageKeydown, true)
      window.addEventListener('focusin', this.#rememberActiveComposer, true)
      window.addEventListener('pointerdown', this.#rememberActiveComposer, true)
    }
  }

  disconnect() {
    this.listenerTarget.removeEventListener(
      'keydown',
      this.#handleKeydown,
      this.composerScope
    )
    if (window.SimpleroCoachCommands?.run === this.#run)
      delete window.SimpleroCoachCommands
    pageComposers.delete(this)
    if (activePageComposer === this) activePageComposer = null
    window.removeEventListener('keydown', this.#handlePageKeydown, true)
    window.removeEventListener('focusin', this.#rememberActiveComposer, true)
    window.removeEventListener(
      'pointerdown',
      this.#rememberActiveComposer,
      true
    )
  }

  #availableOnPage() {
    return (
      this.element.isConnected &&
      this.element.getClientRects().length > 0 &&
      window.getComputedStyle(this.element).visibility !== 'hidden' &&
      !this.element.closest('[inert]')
    )
  }

  #rememberActiveComposer = (event) => {
    const conversation = event.target?.closest?.('.js-chat-bot-conversation')
    if (
      (this.element.contains(event.target) ||
        (conversation &&
          this.element.closest('.js-chat-bot-conversation') ===
            conversation)) &&
      this.#availableOnPage()
    ) {
      activePageComposer = this
    }
  }

  #handlePageKeydown = (event) => {
    if (event.isComposing || event.repeat || event.defaultPrevented) return
    // Focused composers keep their local capture handler, including inline edits
    // and composers inside modals. Other open layers keep ownership of their keys,
    // and other rich-text editors keep theirs (Ctrl+Shift+S is TipTap strikethrough).
    if (
      event.target?.closest?.('.chat-bot-message-input-component') ||
      event.target?.isContentEditable ||
      event.target?.closest?.('[role="menu"], .pbs-root') ||
      hasAnyOpenLayer()
    )
      return

    const command = this.commandsValue.find(
      (c) =>
        c.scope === 'composer' &&
        c.page_wide &&
        c.binding &&
        eventMatchesBinding(event, c.binding)
    )
    if (!command) return

    const available = [...pageComposers].filter((c) => c.#availableOnPage())
    const conversation = event.target?.closest?.('.js-chat-bot-conversation')
    // Never fall through to another conversation when this one cannot respond.
    const owner = conversation
      ? available.find(
          (c) => c.element.closest('.js-chat-bot-conversation') === conversation
        )
      : available.includes(activePageComposer)
      ? activePageComposer
      : available[0]
    if (owner !== this) return

    this.#dispatchCommand(event, command)
  }

  #handleKeydown = (event) => {
    if (event.isComposing || event.repeat || event.defaultPrevented) return
    // A modal / bottom sheet / dropdown open on top of the page should own the keyboard —
    // Escape still works (esc_stack handles it independently of this controller).
    // Local bindings still work inside a modal containing a composer. A picker's
    // search field owns its own keys, however, even if nested in that composer:
    // desktop menus are [role="menu"]; the mobile bottom sheet mounts a .pbs-root.
    if (this.composerScope) {
      if (event.target?.closest('[role="menu"], .pbs-root')) return
    } else if (hasAnyOpenLayer()) return

    const command = this.commandsValue.find(
      (c) =>
        (c.scope === 'composer') === this.composerScope &&
        c.binding &&
        eventMatchesBinding(event, c.binding) &&
        !(c.in_text_fields === false && isEditable(event.target))
    )
    if (!command) return

    this.#dispatchCommand(event, command)
  }

  // A global command by key, from the app: 'handled', 'suppressed' (something open or a field
  // has the key, as a keystroke would find), or 'unhandled' (nothing on this page takes it, so
  // the app may load a page that does).
  #run = (key) => {
    const command = this.commandsValue.find(
      (c) => c.scope !== 'composer' && c.key === key
    )
    if (!command) return 'unhandled'
    if (hasAnyOpenLayer()) return 'suppressed'
    if (command.in_text_fields === false && isEditable(document.activeElement))
      return 'suppressed'

    const commandEvent = new CustomEvent(command.event, {
      bubbles: true,
      cancelable: true,
    })
    document.dispatchEvent(commandEvent)
    return commandEvent.defaultPrevented ? 'handled' : 'unhandled'
  }

  #dispatchCommand(event, command) {
    const commandEvent = new CustomEvent(command.event, {
      bubbles: true,
      cancelable: true,
    })
    if (this.composerScope) {
      this.element.dispatchEvent(commandEvent)
      // Missing, locked, or hidden controls leave the keystroke alone.
      if (!commandEvent.defaultPrevented) return
      event.stopPropagation()
    } else {
      document.dispatchEvent(commandEvent)
      // Nothing on the page took it (no search to open): the browser keeps the key (⌘F: find).
      if (!commandEvent.defaultPrevented) return
    }
    event.preventDefault()
  }
}

// A field or editor, which keeps the keys it uses (⌘I: italic).
function isEditable(target) {
  return (
    !!target &&
    (target.isContentEditable ||
      !!target.closest?.('input, textarea, select, [contenteditable]'))
  )
}

// A binding is e.g. 'Mod+Shift+O' — modifiers, then the key. Mnemonic letters match event.key:
// event.code is the physical QWERTY position, so it puts them on the wrong key under Dvorak.
// Number-row shortcuts are positional instead and match Digit1..Digit9; with Shift held,
// event.key is punctuation on many layouts (e.g. "!" for Shift+1), not the displayed digit.
export function eventMatchesBinding(event, binding) {
  const parts = binding.split('+')
  const key = parts.at(-1) === 'Space' ? ' ' : parts.at(-1)
  const modifiers = new Set(parts.slice(0, -1))

  const keyMatches = /^\d$/.test(key)
    ? event.code === `Digit${key}`
    : event.key?.toUpperCase() === key.toUpperCase()
  if (!keyMatches) return false

  const wantMeta = modifiers.has('Mod') ? IS_MAC : modifiers.has('Meta')
  const wantCtrl = modifiers.has('Mod') ? !IS_MAC : modifiers.has('Ctrl')

  return (
    event.metaKey === wantMeta &&
    event.ctrlKey === wantCtrl &&
    event.shiftKey === modifiers.has('Shift') &&
    event.altKey === modifiers.has('Alt')
  )
}
